import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface ScoreResultProps {
  score: number;
  maxScore: number;
  answers: number[];
  onRetake: () => void;
}

const getScoreProfile = (pct: number) => {
  if (pct >= 90) return {
    level: 'CATASTROPHIC',
    color: 'text-red-400',
    borderColor: 'border-red-500/60',
    bgColor: 'bg-red-500/10',
    barColor: 'bg-red-500',
    icon: 'ri-alarm-warning-fill',
    headline: 'Your Situation Is Gloriously Dire',
    description: 'Congratulations. You have achieved what our analysts call "Peak Problem Density." Your circumstances are so thoroughly complicated that our senior team will need to convene an emergency sub-committee just to begin identifying the sub-problems nested within your primary problems. This is, frankly, our favorite kind of client.',
    badge: 'PLATINUM PROBLEM TIER',
    bonusProblems: 12,
  };
  if (pct >= 75) return {
    level: 'SEVERE',
    color: 'text-orange-400',
    borderColor: 'border-orange-500/60',
    bgColor: 'bg-orange-500/10',
    barColor: 'bg-orange-500',
    icon: 'ri-error-warning-fill',
    headline: 'Alarmingly Promising Complexity',
    description: 'Our diagnostics indicate a rich, multi-layered problem ecosystem with exceptional growth potential. With the right over-thinking applied by our team of dangerously over-educated specialists, we project your current situation can be expanded into no fewer than 8 distinct, fully-documented crises within 90 days.',
    badge: 'GOLD PROBLEM TIER',
    bonusProblems: 8,
  };
  if (pct >= 55) return {
    level: 'ELEVATED',
    color: 'text-amber-400',
    borderColor: 'border-amber-500/60',
    bgColor: 'bg-amber-500/10',
    barColor: 'bg-amber-400',
    icon: 'ri-alert-fill',
    headline: 'Significant Untapped Problem Potential',
    description: 'Your problems show real promise but remain frustratingly under-complicated. Our 14-framework analysis methodology has identified at least 6 dimensions of your situation that you have not yet begun to worry about. We consider this a tremendous opportunity and are excited to help you realize it.',
    badge: 'SILVER PROBLEM TIER',
    bonusProblems: 5,
  };
  if (pct >= 35) return {
    level: 'MODERATE',
    color: 'text-yellow-400',
    borderColor: 'border-yellow-500/40',
    bgColor: 'bg-yellow-500/10',
    barColor: 'bg-yellow-400',
    icon: 'ri-information-fill',
    headline: 'Modest Problems With Room To Grow',
    description: 'While your current problem portfolio is underwhelming by our standards, do not be discouraged. Our team has successfully transformed situations far simpler than yours into sprawling, multi-generational crises. With consistent over-thinking and a structured amplification plan, your problems have real potential.',
    badge: 'BRONZE PROBLEM TIER',
    bonusProblems: 3,
  };
  return {
    level: 'NEGLIGIBLE',
    color: 'text-green-400',
    borderColor: 'border-green-500/40',
    bgColor: 'bg-green-500/10',
    barColor: 'bg-green-400',
    icon: 'ri-checkbox-circle-fill',
    headline: 'Dangerously Under-Problemed',
    description: 'Our algorithms are deeply concerned. You appear to be living with an irresponsible lack of complexity. This is not a compliment. People with scores this low are statistically at high risk of contentment, which our research shows is the leading precursor to stagnation. We urge you to contact us immediately.',
    badge: 'EMERGENCY INTAKE REQUIRED',
    bonusProblems: 3,
  };
};

const ScoreResult = ({ score, maxScore, onRetake }: ScoreResultProps) => {
  const pct = Math.round((score / maxScore) * 100);
  const profile = getScoreProfile(pct);

  const insights = [
    { label: 'Problem Complexity Index', value: `${Math.min(pct + 12, 100)}%`, icon: 'ri-bar-chart-fill' },
    { label: 'Overthinking Receptivity', value: `${Math.min(pct + 8, 100)}%`, icon: 'ri-brain-fill' },
    { label: 'Bonus Problem Eligibility', value: `+${profile.bonusProblems} Guaranteed`, icon: 'ri-add-circle-fill' },
    { label: 'Crisis Escalation Potential', value: pct >= 75 ? 'Exceptional' : pct >= 50 ? 'High' : 'Developing', icon: 'ri-arrow-up-double-fill' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full"
    >
      {/* Score Badge */}
      <div className="text-center mb-10">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
          className={`inline-flex items-center justify-center w-28 h-28 rounded-full border-4 ${profile.borderColor} ${profile.bgColor} mb-6`}
        >
          <i className={`${profile.icon} text-5xl ${profile.color}`}></i>
        </motion.div>

        <div className={`inline-block text-xs font-black tracking-widest uppercase px-4 py-1.5 rounded-full border ${profile.borderColor} ${profile.bgColor} ${profile.color} mb-4`}>
          {profile.badge}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-end justify-center gap-2 mb-2">
            <span className="text-8xl font-black text-white leading-none">{pct}</span>
            <span className="text-3xl font-bold text-cream/50 mb-3">/ 100</span>
          </div>
          <p className={`text-lg font-black tracking-widest uppercase ${profile.color}`}>
            {profile.level} SEVERITY
          </p>
        </motion.div>
      </div>

      {/* Score Bar */}
      <div className="mb-10">
        <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
          <motion.div
            className={`h-3 rounded-full ${profile.barColor}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-cream/40">
          <span>Suspiciously Fine</span>
          <span>Gloriously Catastrophic</span>
        </div>
      </div>

      {/* Headline & Description */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className={`rounded-2xl border ${profile.borderColor} ${profile.bgColor} p-8 mb-8`}
      >
        <h3 className="text-2xl font-black text-white mb-3 font-serif">{profile.headline}</h3>
        <p className="text-cream/75 leading-relaxed">{profile.description}</p>
      </motion.div>

      {/* Insight Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        className="grid grid-cols-2 gap-4 mb-10"
      >
        {insights.map((item, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="w-8 h-8 flex items-center justify-center mb-2">
              <i className={`${item.icon} text-gold text-xl`}></i>
            </div>
            <p className="text-xs text-cream/50 mb-1">{item.label}</p>
            <p className="text-base font-black text-white">{item.value}</p>
          </div>
        ))}
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="bg-gold/10 border border-gold/30 rounded-xl p-4 mb-8 text-center"
      >
        <p className="text-gold text-xs font-semibold tracking-wide uppercase mb-1">Official Diagnostic Notice</p>
        <p className="text-cream/60 text-xs leading-relaxed">
          This score was calculated using our proprietary 14-framework Problem Severity Matrix™ and should be considered alarming regardless of the number displayed. All scores are final. All scores are also deeply concerning. A Problem Awareness Specialist will be in touch.
        </p>
      </motion.div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/contact"
          className="flex-1 text-center bg-gold text-black px-8 py-4 rounded-full font-black text-base hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          Get Emergency Consultation
        </Link>
        <button
          onClick={onRetake}
          className="flex-1 text-center border-2 border-white/20 text-cream px-8 py-4 rounded-full font-bold text-base hover:border-gold hover:text-gold transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          Retake Assessment
        </button>
      </div>
    </motion.div>
  );
};

export default ScoreResult;
